import { NavLink, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard,
  Package,
  Cpu,
  Radio,
  Users,
  Shield,
  Settings,
  LogOut,
  Server,
  Building2,
  ChevronLeft,
  ChevronRight,
  X,
  Activity,
  ShieldCheck,
  Bot,
  ScrollText,
  Archive,
  ShieldAlert,
} from 'lucide-react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { useTenant } from '../../hooks/useTenant'
import { useAuth } from '../../contexts/AuthContext'
import { useTheme } from '../../contexts/ThemeContext'
import BrandLogo from '../BrandLogo'

const baseNavigation = [
  { key: 'dashboard', href: '/', icon: LayoutDashboard },
  { key: 'applications', href: '/applications', icon: Package },
  { key: 'devices', href: '/devices', icon: Cpu },
  { key: 'gateways', href: '/gateways', icon: Radio },
  { key: 'managedDevices', href: '/managed-devices', icon: Server },
  { key: 'users', href: '/users', icon: Users, adminOnly: true },
  { key: 'redesVirtuais', href: '/redes-virtuais', icon: Shield },
  { key: 'domains', href: '/domains', icon: Building2, adminOnly: true },
  { key: 'audit', href: '/audit', icon: Activity },
  { key: 'deviceLogs', href: '/logs', icon: ScrollText },
  { key: 'backups', href: '/backups', icon: Archive },
  { key: 'security', href: '/security', icon: ShieldAlert },
  { key: 'tpm', href: '/tpm', icon: ShieldCheck, adminOnly: true },
  // globalOnly (não adminOnly): o agente tem root no servidor e credenciais de todos os
  // tenants — admin de tenant não pode enxergar isso.
  { key: 'gestaoIa', href: '/gestao-ia', icon: Bot, globalOnly: true },
]

export default function Sidebar({
  isCollapsed = false,
  onToggleCollapsed,
  isMobileOpen = false,
  onCloseMobile,
}) {
  const { t } = useTranslation()
  const { data: tenant, isLoading: isLoadingTenant } = useTenant()
  const { user, logout } = useAuth()
  const { customization } = useTheme()
  const navigate = useNavigate()

  const bdMode = customization?.logoMenuBackdropMode || customization?.LogoMenuBackdropMode || 'transparent'
  const logoBackdropSolid = bdMode === 'solid'
  const logoBackdropColor =
    customization?.logoMenuBackdropColor || customization?.LogoMenuBackdropColor || '#ffffff'

  // Gerar iniciais do tenant a partir do nome
  const getTenantInitials = (name) => {
    if (!name) return '??'
    const words = name.trim().split(/\s+/)
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
  }

  // Obter role do usuário (pode vir do backend ou ser mapeado)
  const getUserRole = () => {
    // TODO: Quando o backend retornar a role do usuário, usar aqui
    // Por enquanto, retornar 'Owner' como padrão
    return user?.role || t('layout.sidebar.roleDefault')
  }

  const tenantInitials = tenant?.name ? getTenantInitials(tenant.name) : '??'
  const tenantName = tenant?.name || t('layout.sidebar.loading')
  const userRole = getUserRole()
  const isGlobalUser = Boolean(user?.isGlobalUser || user?.IsGlobalUser)
  const isAdminOrAbove = isGlobalUser
    || /^(admin|owner|global)$/i.test(userRole)

  const navigation = baseNavigation.filter((item) => {
    if (item.globalOnly && !isGlobalUser) return false
    return !item.adminOnly || isAdminOrAbove
  })

  const renderSidebarContent = (mobile = false) => (
    <div
      className="flex flex-col flex-grow overflow-y-auto shadow-purple-lg h-full"
      style={{ background: 'var(--theme-menu-background)' }}
    >
      <div
        className={clsx(
          'py-5 px-3 border-b border-white/10 relative flex justify-center'
        )}
        style={
          logoBackdropSolid
            ? { backgroundColor: logoBackdropColor }
            : undefined
        }
      >
        {!mobile && onToggleCollapsed && (
          <button
            type="button"
            onClick={onToggleCollapsed}
            className="absolute right-2 top-2 p-1.5 rounded-theme-md text-white/80 hover:text-white hover:bg-white/10 transition-colors"
            title={isCollapsed ? t('layout.sidebar.expandTitle') : t('layout.sidebar.collapseTitle')}
            aria-label={isCollapsed ? t('layout.sidebar.expandAria') : t('layout.sidebar.collapseAria')}
          >
            {isCollapsed ? (
              <ChevronRight className="w-4 h-4" />
            ) : (
              <ChevronLeft className="w-4 h-4" />
            )}
          </button>
        )}

        {mobile && onCloseMobile && (
          <button
            type="button"
            onClick={onCloseMobile}
            className="absolute right-2 top-2 p-1.5 rounded-theme-md text-white/80 hover:text-white hover:bg-white/10 transition-colors"
            aria-label={t('layout.sidebar.closeAria')}
          >
            <X className="w-4 h-4" />
          </button>
        )}

        <div className="flex flex-col items-center">
          <BrandLogo
            className={clsx(
              'object-contain drop-shadow-[0_2px_10px_rgba(0,0,0,0.2)]',
              isCollapsed && !mobile
                ? 'h-10 w-10'
                : 'h-[4em] w-auto max-w-[min(100%,280px)] object-right'
            )}
            alt="Automais"
            compact={isCollapsed && !mobile}
          />
        </div>
      </div>

      {/* Tenant Info */}
      <div className={clsx('px-4 py-4 bg-white/10', isCollapsed && !mobile && 'px-2')}>
        <div className={clsx('flex items-center', isCollapsed && !mobile ? 'justify-center' : 'gap-3')}>
          <div className="w-10 h-10 bg-white/20 rounded-theme-md flex items-center justify-center font-semibold" style={{ color: 'var(--theme-menu-text)' }}>
            {isLoadingTenant ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              tenantInitials
            )}
          </div>
          {(!isCollapsed || mobile) && (
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" style={{ color: 'var(--theme-menu-text)' }}>
                {isLoadingTenant ? t('layout.sidebar.loading') : tenantName}
              </div>
              <div className="text-xs" style={{ color: 'color-mix(in srgb, var(--theme-menu-text) 70%, transparent)' }}>{userRole}</div>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className={clsx('flex-1 py-4 space-y-1', isCollapsed && !mobile ? 'px-2' : 'px-3')}>
        {navigation.map((item) => {
          const label = t(`layout.sidebar.${item.key}`)
          return (
          <NavLink
            key={item.key}
            to={item.href}
            end={item.href === '/'}
            onClick={mobile ? onCloseMobile : undefined}
            title={isCollapsed && !mobile ? label : undefined}
            className={({ isActive }) =>
              clsx(
                'flex items-center px-3 py-2.5 text-sm font-medium rounded-theme-md transition-all duration-200',
                isCollapsed && !mobile ? 'justify-center' : 'gap-3',
                isActive
                  ? 'shadow-lg'
                  : ''
              )
            }
            style={({ isActive }) =>
              isActive
                ? { backgroundColor: 'var(--theme-menu-active-bg)', color: 'var(--theme-menu-active-text, var(--theme-primary))' }
                : { color: 'var(--theme-menu-text)' }
            }
          >
            <item.icon className="w-5 h-5 flex-shrink-0" />
            {(!isCollapsed || mobile) && label}
          </NavLink>
          )
        })}
      </nav>

      {/* Bottom Actions */}
      <div className={clsx('pb-4 space-y-1 pt-4', isCollapsed && !mobile ? 'px-2' : 'px-3')} style={{ borderTopColor: 'color-mix(in srgb, var(--theme-menu-text) 15%, transparent)', borderTopWidth: '1px' }}>
        <NavLink
          to="/settings"
          onClick={mobile ? onCloseMobile : undefined}
          className={({ isActive }) =>
            clsx(
              'w-full px-3 py-2.5 text-sm font-medium rounded-theme-md transition-all duration-200 flex items-center',
              isCollapsed && !mobile ? 'justify-center' : 'gap-3',
              isActive
                ? 'shadow-lg'
                : ''
            )
          }
          style={({ isActive }) =>
            isActive
              ? { backgroundColor: 'var(--theme-menu-active-bg)', color: 'var(--theme-menu-active-text, var(--theme-primary))' }
              : { color: 'var(--theme-menu-text)' }
          }
          title={isCollapsed && !mobile ? t('layout.sidebar.settings') : undefined}
        >
          <Settings className="w-5 h-5" />
          {(!isCollapsed || mobile) && t('layout.sidebar.settings')}
        </NavLink>
        <button
          type="button"
          onClick={() => {
            logout()
            navigate('/login')
          }}
          className={clsx(
            'w-full px-3 py-2.5 text-sm font-medium hover:bg-white/10 rounded-theme-md transition-all duration-200 flex items-center',
            isCollapsed && !mobile ? 'justify-center' : 'gap-3'
          )}
          style={{ color: 'var(--theme-menu-text)' }}
          title={isCollapsed && !mobile ? t('layout.sidebar.logout') : undefined}
        >
          <LogOut className="w-5 h-5" />
          {(!isCollapsed || mobile) && t('layout.sidebar.logout')}
        </button>
      </div>
    </div>
  )

  return (
    <>
      {/* Desktop Sidebar */}
      <div
        className={clsx(
          'hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col transition-all duration-300',
          isCollapsed ? 'lg:w-20' : 'lg:w-72'
        )}
      >
        {renderSidebarContent(false)}
      </div>

      {/* Mobile Sidebar */}
      {isMobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden flex">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={onCloseMobile}
            aria-hidden="true"
          />
          <div className="relative w-72 max-w-[85vw] h-full">
            {renderSidebarContent(true)}
          </div>
        </div>
      )}
    </>
  )
}

